/**
 * OneClick V2 Anti-DDoS 配置缓存
 *
 * 策略:
 * - 应用启动时调用 refreshAntiDdosConfig() 加载配置
 * - 每 60 秒自动刷新（与服务端刷新周期同步）
 * - 签名时从本地缓存读取，无需每次签名前请求
 * - 服务端保留 2 轮配置（当前轮 + 上一轮），120 秒内提交都能匹配
 */

import { tradingV2ControllerApi } from '@ui/services/backend/clients/clients';
import type { AntiDdosConfigResponse } from '@ui/services/backend/oneClickServiceGenerated/models';

let antiDdosCache: AntiDdosConfigResponse[] = [];
let antiDdosCacheTs = 0;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

/** 从服务端拉取最新 Anti-DDoS 配置 */
export async function refreshAntiDdosConfig(): Promise<
  AntiDdosConfigResponse[]
> {
  const data = await tradingV2ControllerApi.getAntiDdosConfig();
  antiDdosCache = data;
  antiDdosCacheTs = Date.now();
  return data;
}

/** 启动定时刷新（60s 间隔） */
export function startAntiDdosConfigRefresh(): void {
  stopAntiDdosConfigRefresh();

  // 首次加载
  refreshAntiDdosConfig().catch((err) => {
    console.warn('[OneClickV2] Initial anti-ddos config load failed:', err);
  });

  // 每 60 秒刷新
  refreshTimer = setInterval(() => {
    refreshAntiDdosConfig().catch((err) => {
      console.warn('[OneClickV2] Anti-ddos config refresh failed:', err);
    });
  }, 60_000);
}

/** 停止定时刷新 */
export function stopAntiDdosConfigRefresh(): void {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * 从本地缓存读取指定 action 的 Anti-DDoS 配置
 *
 * 如果该 action 的 Anti-DDoS 未启用，返回 feeToken=address(0), antiDdosFee="0"
 */
export function getCachedAntiDdosConfig(action: number): {
  feeToken: `0x${string}`;
  antiDdosFee: bigint;
} {
  const config = antiDdosCache.find((c) => c.action === action);

  if (!config || !config.enabled) {
    return {
      feeToken: '0x0000000000000000000000000000000000000000',
      antiDdosFee: 0n,
    };
  }

  return {
    feeToken: config.feeToken as `0x${string}`,
    antiDdosFee: BigInt(config.antiDdosFee),
  };
}

/** 检查配置缓存是否已加载 */
export function isAntiDdosConfigLoaded(): boolean {
  return antiDdosCache.length > 0;
}

/** 获取缓存时间戳（用于调试） */
export function getAntiDdosConfigTimestamp(): number {
  return antiDdosCacheTs;
}
